import React, { useRef, useCallback } from 'react';
import { SnapList, SnapItem, useScroll, useDragToScroll } from 'react-snaplist-carousel';
import { useTranslation } from 'react-i18next';
import { FeatureCard } from '../feature-card';
import { useCarouselPadding } from '../../../shared/hooks/use-carousel-padding';
import { CARD_GAP } from '../../constants';
import { TabData } from '../../tab-data';
import { TabTranslation } from '../../types';

interface FeaturesHorizontalCarouselProps {
  featuresData: TabData[];
  featuresTranslations: TabTranslation[];
}

const FeaturesHorizontalCarousel: React.FC<FeaturesHorizontalCarouselProps> = ({
  featuresData,
  featuresTranslations,
}) => {
  const snapListRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();

  const goToChildren = useScroll({ ref: snapListRef });
  useDragToScroll({ ref: snapListRef });

  const paddingHorizontal = useCarouselPadding();

  const handleFeatureButtonClick = useCallback((featureId: string) => {
    if (typeof window === 'undefined') return;

    if (featureId === 'ready-homes') {
      const lang = i18n.language || 'en';
      window.location.href = `/${lang}/shared-houses`;
    }
  }, [i18n.language]);

  if (featuresData.length === 0) {
    return (
      <div className="mb-7 mt-10 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-text-secondary">
            {t('js.features.marketplace.home.third_section.features_list.empty')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <SnapList
      ref={snapListRef as any}
      direction="horizontal"
      className="mb-7 mt-10"
    >
      {featuresData.map((feature, idx) => {
        const translation = featuresTranslations[idx] || {};

        return (
          <SnapItem
            key={feature.id}
            margin={{
              left: idx === 0 ? `${paddingHorizontal}px` : `${CARD_GAP}px`,
              right: idx === featuresData.length - 1 ? `${paddingHorizontal}px` : `${CARD_GAP}px`,
            }}
            snapAlign="center"
          >
            <FeatureCard
              featureId={feature.id}
              icon={feature.icon({})}
              title={translation.title || ''}
              description={translation.description || ''}
              details={translation.details || ''}
              buttonText={translation.buttonText || ''}
              showAppStoreButtons={idx === 0}
              onButtonClick={() => handleFeatureButtonClick(feature.id)}
              onClick={() => goToChildren(idx)}
            />
          </SnapItem>
        );
      })}
    </SnapList>
  );
};

export { FeaturesHorizontalCarousel };
